import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import StarRating from "../components/StarRating";
import api, { formatApiError } from "../lib/api";
import { fullUrl } from "../lib/uploads";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import {
  ShieldCheck, MapPin, ChatCircle, Star, Briefcase, CalendarBlank,
  Globe, FacebookLogo, InstagramLogo, CaretLeft,
} from "@phosphor-icons/react";

const YEARS_LABEL = {
  "0-1": "Less than 1 year",
  "1-2": "1–2 years",
  "3-5": "3–5 years",
  "6-10": "6–10 years",
  "10+": "10+ years",
};

export default function TradesPersonProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/tradespeople/${id}`)
      .then(({ data }) => setT(data))
      .catch((err) => {
        console.error("[trader-profile] failed to load:", err);
        setT(null);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const tradeNames = useMemo(() => {
    // Tradespeople have trade slugs; we map them via /api/meta/trades cache if available later.
    return t?.trades || [];
  }, [t]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/login?next=" + encodeURIComponent(window.location.pathname));
      return;
    }
    if (!msg.trim()) return;
    try {
      await api.post("/messages", { recipient_id: id, body: msg });
      toast.success("Message sent");
      setMsg("");
      navigate("/messages");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) {
    return <Layout><div className="max-w-4xl mx-auto p-10 text-[var(--text-muted)]">Loading…</div></Layout>;
  }
  if (!t) {
    return <Layout><div className="max-w-4xl mx-auto p-10">Tradesperson not found.</div></Layout>;
  }

  const totalReviews = (t.reviews || []).length;
  const dist = t.rating_distribution || { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  const memberSince = t.created_at ? new Date(t.created_at) : null;
  const social = t.social_links || {};

  return (
    <Layout>
      {/* Cover photo banner */}
      <div className="relative h-40 sm:h-56"
           style={{
             background: t.cover_photo
               ? `center/cover no-repeat url(${fullUrl(t.cover_photo)})`
               : "linear-gradient(135deg, #11523B 0%, #1a6f4e 100%)",
           }}
           data-testid="trader-cover">
        <button onClick={() => navigate(-1)}
                className="absolute top-3 left-3 bg-white/95 text-[var(--brand-green)] w-9 h-9 rounded-full flex items-center justify-center shadow"
                data-testid="trader-back-btn"
                aria-label="Back">
          <CaretLeft size={18} weight="bold" />
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-5 lg:px-8 -mt-12 sm:-mt-16 pb-12">
        {/* Profile header */}
        <div className="brand-card p-5 sm:p-7" data-testid="trader-profile">
          <div className="flex flex-col sm:flex-row gap-5 sm:items-end">
            {t.picture ? (
              <img src={fullUrl(t.picture)} alt={t.name}
                   className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl object-cover border-4 border-white shadow"
                   data-testid="trader-photo" />
            ) : (
              <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl bg-[rgba(17,82,59,0.1)] text-[var(--brand-green)] font-display font-extrabold text-3xl flex items-center justify-center border-4 border-white shadow">
                {(t.name || "?")[0].toUpperCase()}
              </div>
            )}
            <div className="flex-1">
              <h1 className="font-display font-extrabold text-2xl sm:text-3xl leading-tight" style={{ color: "var(--brand-green)" }}>
                {t.business_name || t.name}
              </h1>
              {t.business_name && t.name && <p className="text-sm text-[var(--text-muted)]">{t.name}</p>}
              <div className="mt-2 flex items-center gap-3 flex-wrap text-sm">
                <StarRating value={t.rating_avg || 0} count={t.rating_count || 0} />
                {t.verified && (
                  <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-[rgba(17,82,59,0.1)] text-[var(--brand-green)]"
                        data-testid="verified-badge">
                    <ShieldCheck size={12} weight="fill" className="inline -mt-0.5 mr-0.5" /> Verified
                  </span>
                )}
                {t.postcode_area && (
                  <span className="text-[var(--text-muted)] inline-flex items-center gap-1">
                    <MapPin size={14} /> {t.postcode_area} · {t.work_range_miles} mi
                  </span>
                )}
                {memberSince && (
                  <span className="text-[var(--text-muted)] inline-flex items-center gap-1">
                    <CalendarBlank size={14} /> Member since {memberSince.toLocaleString("en-GB", { month: "short", year: "numeric" })}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Services chips */}
          {tradeNames.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="trader-services">
              {tradeNames.map((s) => (
                <span key={s} className="px-2.5 py-1 rounded-md text-xs font-semibold bg-[rgba(226,109,41,0.1)] text-[var(--brand-orange)]">
                  {humanizeSlug(s)}
                </span>
              ))}
            </div>
          )}

          {/* Stats row */}
          <div className="mt-6 grid grid-cols-3 gap-3" data-testid="trader-stats">
            <StatCard
              value={t.years_experience ? YEARS_LABEL[t.years_experience] || t.years_experience : "—"}
              label="Experience"
            />
            <StatCard
              value={`${t.jobs_completed || 0}`}
              label="Jobs completed"
              Icon={Briefcase}
            />
            <StatCard
              value={(t.rating_avg || 0).toFixed(1)}
              label="Average rating"
              Icon={Star}
              accent
            />
          </div>

          {/* About / Bio */}
          {t.bio && (
            <div className="mt-6">
              <h3 className="font-display font-extrabold text-base text-[var(--brand-green)]">About</h3>
              <p className="mt-2 text-[var(--text-main)] leading-relaxed whitespace-pre-line">{t.bio}</p>
            </div>
          )}

          {/* Qualifications */}
          {t.qualifications && (
            <div className="mt-6">
              <h3 className="font-display font-extrabold text-base text-[var(--brand-green)]">Qualifications &amp; Registrations</h3>
              <p className="mt-2 text-sm text-[var(--text-main)]">{t.qualifications}</p>
            </div>
          )}

          {/* Service areas */}
          {(t.service_areas || []).length > 0 && (
            <div className="mt-6">
              <h3 className="font-display font-extrabold text-base text-[var(--brand-green)]">Service areas</h3>
              <div className="mt-2 flex flex-wrap gap-2">
                {t.service_areas.map((a) => (
                  <span key={a} className="px-2.5 py-1 rounded-md text-xs font-bold bg-[#F4FDF7] text-[var(--brand-green)] border border-[#CBE7D6]">{a}</span>
                ))}
              </div>
            </div>
          )}

          {/* Social links */}
          {(social.website || social.facebook || social.instagram) && (
            <div className="mt-6 flex flex-wrap gap-2">
              {social.website && (
                <a href={social.website} target="_blank" rel="noopener noreferrer"
                   className="btn-ghost text-sm" data-testid="trader-website">
                  <Globe size={14} weight="duotone" /> Website
                </a>
              )}
              {social.facebook && (
                <a href={social.facebook} target="_blank" rel="noopener noreferrer"
                   className="btn-ghost text-sm" data-testid="trader-facebook">
                  <FacebookLogo size={14} weight="duotone" /> Facebook
                </a>
              )}
              {social.instagram && (
                <a href={social.instagram} target="_blank" rel="noopener noreferrer"
                   className="btn-ghost text-sm" data-testid="trader-instagram">
                  <InstagramLogo size={14} weight="duotone" /> Instagram
                </a>
              )}
            </div>
          )}
        </div>

        {/* Portfolio gallery */}
        {(t.portfolio || []).length > 0 && (
          <section className="mt-6 brand-card p-5 sm:p-7" data-testid="trader-portfolio">
            <h2 className="font-display font-extrabold text-xl text-[var(--brand-green)]">Recent work</h2>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
              {t.portfolio.map((p, i) => (
                <button key={p} type="button" onClick={() => setLightbox(p)}
                        className="block rounded-lg overflow-hidden border border-[var(--border-stone)] hover:opacity-90 transition"
                        data-testid={`trader-portfolio-${i}`}>
                  <img src={fullUrl(p)} alt={`Work ${i + 1}`} className="w-full h-28 sm:h-32 object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Reviews */}
        <section className="mt-6 brand-card p-5 sm:p-7" data-testid="trader-reviews">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h2 className="font-display font-extrabold text-xl text-[var(--brand-green)]">Reviews</h2>
            <span className="text-sm text-[var(--text-muted)]">
              {(t.rating_avg || 0).toFixed(1)} ★ from {totalReviews} review{totalReviews === 1 ? "" : "s"}
            </span>
          </div>

          {totalReviews === 0 ? (
            <p className="text-[var(--text-muted)] mt-3 text-sm">No reviews yet — be the first.</p>
          ) : (
            <>
              {/* Rating distribution */}
              <div className="mt-4 space-y-1.5">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = dist[star] || 0;
                  const pct = totalReviews ? (count / totalReviews) * 100 : 0;
                  return (
                    <div key={star} className="flex items-center gap-3 text-xs">
                      <span className="w-3 text-[var(--text-muted)] font-bold">{star}</span>
                      <Star size={12} weight="fill" className="text-[var(--brand-orange)]" />
                      <div className="flex-1 h-2 bg-[#F0EEE7] rounded-full overflow-hidden">
                        <div className="h-full bg-[var(--brand-orange)]" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-6 text-right text-[var(--text-muted)]">{count}</span>
                    </div>
                  );
                })}
              </div>

              {/* Review list */}
              <div className="mt-5 space-y-3">
                {(t.reviews || []).map((r) => (
                  <article key={r.review_id} className="border border-[var(--border-stone)] rounded-xl p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-semibold text-[var(--brand-green)]">{r.customer_name || "Customer"}</span>
                      <StarRating value={r.rating} />
                    </div>
                    {r.comment && <p className="mt-2 text-sm text-[var(--text-main)] leading-relaxed">{r.comment}</p>}
                    {r.created_at && (
                      <p className="mt-2 text-xs text-[var(--text-muted)]">{new Date(r.created_at).toLocaleDateString("en-GB")}</p>
                    )}
                  </article>
                ))}
              </div>
            </>
          )}
        </section>

        {/* Contact form */}
        {user && user.user_id !== t.user_id && (
          <section className="mt-6 brand-card p-5 sm:p-7">
            <h2 className="font-display font-extrabold text-xl text-[var(--brand-green)]">Send a message</h2>
            <form onSubmit={sendMessage} className="mt-3">
              <textarea
                data-testid="trader-message-input"
                value={msg} onChange={(e) => setMsg(e.target.value)} rows={3}
                placeholder="Hi, are you available next week to look at a leaking tap?"
                className="w-full px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-orange)] resize-y"
              />
              <button className="btn-primary mt-3 w-full sm:w-auto justify-center" data-testid="trader-message-send">
                <ChatCircle size={16} /> Send message
              </button>
            </form>
          </section>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
             onClick={() => setLightbox(null)}
             data-testid="lightbox">
          <img src={fullUrl(lightbox)} alt="" className="max-w-full max-h-full rounded-lg" />
        </div>
      )}
    </Layout>
  );
}

function StatCard({ value, label, Icon, accent }) {
  return (
    <div className="rounded-xl px-3 py-3 text-center border"
         style={{ borderColor: accent ? "#FFD2AE" : "#E4E2DC", background: accent ? "#FFF6EF" : "#FBFAF6" }}>
      <div className="flex items-center justify-center gap-1 font-display font-extrabold text-xl"
           style={{ color: accent ? "var(--brand-orange)" : "var(--brand-green)" }}>
        {Icon && <Icon size={18} weight="duotone" />}
        {value}
      </div>
      <div className="text-xs text-[var(--text-muted)] mt-1">{label}</div>
    </div>
  );
}

function humanizeSlug(slug) {
  return slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
